import { useState } from 'react';
import clsx from 'clsx';
import { Button } from '@nf/ui';
import { Icon } from '@/components/icons/svg-icons';
import { AutoPopupModal } from '@/components/widgets/modal';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useLocale, useTranslations } from 'next-intl';
import { CdnImg } from '@/features/cdn-img';
import styles from './winning-notify-popup.module.scss';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { promotionService } from '@/services/promotion.service';
import { useCwcJackpotSetting } from '@/hooks/query/cwc-jackpot/use-cwc-jackpot-setting';
import { useCwcJackpotWinningNotify } from '@/hooks/query/cwc-jackpot/use-cwc-jackpot-winning-notify';
import { useRouter } from '@/navigation';
import { poppins } from '@/lib/fonts';
import { AutoPopupKey } from '@nf/auto-popup-handler';

export const WinningNotifyPopup = () => {
	const t = useTranslations('General');
	const router = useRouter();
	const [isOpen, setIsOpen] = useState(true);
	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const { convertAmountByCurrency } = useCwcJackpotSetting();
	const { data: winningNotify, setCwcJackpotWinningNotify } = useCwcJackpotWinningNotify();
	const defaultCurrencyId = promotionService.getDefaultCurrencyId();
	const defaultCurrencyName = promotionService.getDefaultCurrencyName();
	const currencyName = user?.LicenseeSiteSetting?.DisplayCurrencyName ?? defaultCurrencyName;
	const convertedPrize = convertAmountByCurrency(winningNotify?.PrizeAmount ?? 0, user?.CurrId ?? defaultCurrencyId);
	const locale = useLocale();
	const isUseNewFont = !['vi-VN'].includes(locale);
	
	const handleClose = async () => {
		setKafkaNormal({
			note: KafkaNote.CWCJackpot,
			type: 1,
			action: 5
		});
		setIsOpen(false);
		setCwcJackpotWinningNotify();
	};

	const handleCheckResult = () => {
		setKafkaNormal({
			note: KafkaNote.CWCJackpot,
			type: 1,
			action: 3
		});
		winningNotify?.GroupId && router.push(`/promotion/score-challenge/my-pools?groupId=${winningNotify?.GroupId}`);
		setIsOpen(false);
		setCwcJackpotWinningNotify();
	}

	const handleJoinNextPool = () => {
		setKafkaNormal({
			note: KafkaNote.CWCJackpot,
			type: 1,
			action: 4
		});
		router.push('/promotion/score-challenge/pool-games');
		setIsOpen(false);
		setCwcJackpotWinningNotify();
	}

	return (
		<AutoPopupModal
			popupKey={AutoPopupKey.CwcJackpotWinningNotify}
			isOpen={isOpen && !!(SiteConfigsData?.EnableCwcJackpot && winningNotify?.GroupId && winningNotify?.PrizeAmount)}
			variant="event"
			closeModal={handleClose}
			showHeader={false}
			className={styles.promoModal}
			isShowClose={true}
			isBackdropClose={false}
		>
			<div className={clsx(styles.modalContent, isUseNewFont && poppins.className)}>
				<div className={styles.closeButton}>
					<Button variant="icon" onClick={handleClose}>
						<Icon name="close-round-line" />
					</Button>
				</div>
				<div className={styles.backgroundWrapper}>
					<CdnImg
						className={clsx(styles.backgroundImage, styles.coinsBg)}
						isGalaxyFolder={true}
						src="/images/promotion/score-challenge/winning-notify-popup/bg-coins.png"
						alt="bg-coins"
						width={360}
						height={640}
					/>
					<CdnImg
						className={clsx(styles.backgroundImage, styles.lightBg)}
						isGalaxyFolder={true}
						src="/images/promotion/score-challenge/winning-notify-popup/bg-light.png"
						alt="bg-light"
						width={376}
						height={579}
					/>
					<CdnImg
						className={clsx(styles.backgroundImage, styles.decoBg)}
						isGalaxyFolder={true}
						src="/images/promotion/score-challenge/winning-notify-popup/bg-deco.png"
						alt="bg-deco"
						width={652}
						height={768}
					/>
				</div>
				<div className={styles.contentWrapper}>
					<CdnImg
						className={styles.icon}
						isGalaxyFolder={true}
						src="/images/promotion/score-challenge/winning-notify-popup/jackpot-icon.png"
						alt="jackpot-icon"
						width={120}
						height={120}
					/>
					<div className={styles.title}>{t('lbl_CWCLoginP01')}</div>
					<div className={styles.description}>{t('lbl_CWCWinningPopUp')}</div> 
					<div className={styles.amount}>
						<div className={styles.amountIcon}>
							<CdnImg
								isGalaxyFolder={true}
								src="/images/promotion/score-challenge/winning-notify-popup/coins.png"
								alt="coins"
								width={24}
								height={24}
							/>
						</div>
						<div className={styles.amountCurrency}>{currencyName}</div>
						<div className={styles.amountValue}>{convertedPrize}</div>
					</div>
					<div className={styles.buttonGroup}>
						<button
							className={clsx(styles.button, styles.checkResultButton)}
							onClick={handleCheckResult}
						>
							<span className={styles.buttonText}>{t('lbl_CWCEventTab03')}</span>
						</button>
						<button
							className={clsx(styles.button, styles.joinNextPoolButton)}
							onClick={handleJoinNextPool}
						>
							<span className={styles.buttonText}>{t('lbl_CWCJoinNextPool')}</span>
						</button>
					</div>
				</div>
			</div>
		</AutoPopupModal>
	)
};